/**
 * @fileoverview Scratch extension for real-time object detection using COCO-SSD model.
 * This extension allows sprites to detect and interact with objects in the webcam feed,
 * supporting 80 different object categories from the COCO dataset.
 */

const BlockType = require("../../extension-support/block-type");
const ArgumentType = require("../../extension-support/argument-type");
const formatMessage = require("format-message");
const Cast = require("../../util/cast");

/**
 * Icon svg to be displayed at left edge of each extension block, encoded as a data URI.
 * @type {string}
 */
const blockIconURI =
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI2IiBmaWxsPSIjRkY2NjMzIi8+PGNpcmNsZSBjeD0iMTMiIGN5PSIxMyIgcj0iNCIgZmlsbD0iI2ZmZiIvPjxjaXJjbGUgY3g9IjI3IiBjeT0iMTMiIHI9IjQiIGZpbGw9IiNmZmYiLz48Y2lyY2xlIGN4PSIyNyIgY3k9IjI3IiByPSI0IiBmaWxsPSIjZmZmIi8+PGNpcmNsZSBjeD0iMTMiIGN5PSIyNyIgcj0iNCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==";

/**
 * Configuration constants for the object detection system.
 * @readonly
 */
const CONFIG = {
    // Video frame dimensions for object detection
    VIDEO_WIDTH: 480,
    VIDEO_HEIGHT: 360,

    // Default detection settings
    DEFAULT_FPS: 10,
    DEFAULT_BOX_COLOR: "#ffffff",
    DEFAULT_BOX_THICKNESS: 5,
    DEFAULT_VIDEO_TRANSPARENCY: 50,

    // CDN URLs for loading TensorFlow.js and COCO-SSD model
    TENSORFLOW_CDN_URL:
        "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@3.21.0/dist/tf.min.js",
    COCO_SSD_CDN_URL:
        "https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.2/dist/coco-ssd.min.js",

    // Canvas positioning (hidden off-screen for processing)
    CANVAS_HIDDEN_POSITION: "-9999px",

    // Font settings for object labels
    LABEL_FONT: "12px Arial",
    LABEL_OFFSET: -2,
};

/**
 * Enum for video state values.
 * @readonly
 * @enum {string}
 */
const VideoState = {
    /** Video is turned off */
    OFF: "off",
    /** Video is turned on */
    ON: "on",
};

/**
 * Enum for bounding box dimensions.
 * @readonly
 * @enum {string}
 */
const BoundingBoxDimension = {
    /** X coordinate of top-left corner */
    X: "x",
    /** Y coordinate of top-left corner */
    Y: "y",
    /** Width of bounding box */
    WIDTH: "width",
    /** Height of bounding box */
    HEIGHT: "height",
};

/**
 * List of 80 object categories from the COCO dataset that can be detected.
 * Maps display names to model class names for object detection.
 * @readonly
 */
const COCO_CATEGORIES = [
    // People and Animals
    { text: "person", value: "person" },
    { text: "bird", value: "bird" },
    { text: "cat", value: "cat" },
    { text: "dog", value: "dog" },
    { text: "horse", value: "horse" },
    { text: "sheep", value: "sheep" },
    { text: "cow", value: "cow" },
    { text: "elephant", value: "elephant" },
    { text: "bear", value: "bear" },
    { text: "zebra", value: "zebra" },
    { text: "giraffe", value: "giraffe" },

    // Vehicles
    { text: "bicycle", value: "bicycle" },
    { text: "car", value: "car" },
    { text: "motorcycle", value: "motorcycle" },
    { text: "airplane", value: "airplane" },
    { text: "bus", value: "bus" },
    { text: "train", value: "train" },
    { text: "truck", value: "truck" },
    { text: "boat", value: "boat" },

    // Traffic and Street Items
    { text: "traffic light", value: "traffic light" },
    { text: "fire hydrant", value: "fire hydrant" },
    { text: "stop sign", value: "stop sign" },
    { text: "parking meter", value: "parking meter" },
    { text: "bench", value: "bench" },

    // Accessories and Bags
    { text: "backpack", value: "backpack" },
    { text: "umbrella", value: "umbrella" },
    { text: "handbag", value: "handbag" },
    { text: "tie", value: "tie" },
    { text: "suitcase", value: "suitcase" },

    // Sports Equipment
    { text: "frisbee", value: "frisbee" },
    { text: "skis", value: "skis" },
    { text: "snowboard", value: "snowboard" },
    { text: "sports ball", value: "sports ball" },
    { text: "kite", value: "kite" },
    { text: "baseball bat", value: "baseball bat" },
    { text: "baseball glove", value: "baseball glove" },
    { text: "skateboard", value: "skateboard" },
    { text: "surfboard", value: "surfboard" },
    { text: "tennis racket", value: "tennis racket" },

    // Kitchen Items
    { text: "bottle", value: "bottle" },
    { text: "wine glass", value: "wine glass" },
    { text: "cup", value: "cup" },
    { text: "fork", value: "fork" },
    { text: "knife", value: "knife" },
    { text: "spoon", value: "spoon" },
    { text: "bowl", value: "bowl" },

    // Food Items
    { text: "banana", value: "banana" },
    { text: "apple", value: "apple" },
    { text: "sandwich", value: "sandwich" },
    { text: "orange", value: "orange" },
    { text: "broccoli", value: "broccoli" },
    { text: "carrot", value: "carrot" },
    { text: "hot dog", value: "hot dog" },
    { text: "pizza", value: "pizza" },
    { text: "donut", value: "donut" },
    { text: "cake", value: "cake" },

    // Furniture
    { text: "chair", value: "chair" },
    { text: "couch", value: "couch" },
    { text: "potted plant", value: "potted plant" },
    { text: "bed", value: "bed" },
    { text: "dining table", value: "dining table" },
    { text: "toilet", value: "toilet" },

    // Electronics
    { text: "TV", value: "TV" },
    { text: "laptop", value: "laptop" },
    { text: "mouse", value: "mouse" },
    { text: "remote", value: "remote" },
    { text: "keyboard", value: "keyboard" },
    { text: "cell phone", value: "cell phone" },
    { text: "microwave", value: "microwave" },
    { text: "oven", value: "oven" },
    { text: "toaster", value: "toaster" },
    { text: "refrigerator", value: "refrigerator" },

    // Household Items
    { text: "sink", value: "sink" },
    { text: "book", value: "book" },
    { text: "clock", value: "clock" },
    { text: "vase", value: "vase" },
    { text: "scissors", value: "scissors" },
    { text: "teddy bear", value: "teddy bear" },
    { text: "hair drier", value: "hair drier" },
    { text: "toothbrush", value: "toothbrush" },
];

/**
 * Scratch extension class for real-time object detection using COCO-SSD model.
 * Provides blocks for detecting objects, measuring distances and angles, and controlling visual feedback.
 */
class Scratch3ObjectDetectionBlocks {
    /**
     * Create a new Object Detection extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        /**
         * The Scratch runtime instance
         * @type {Runtime}
         * @private
         */
        this.runtime = runtime;

        /**
         * Video frame dimensions for object detection
         * @type {number[]}
         * @private
         */
        this.DIMENSIONS = [CONFIG.VIDEO_WIDTH, CONFIG.VIDEO_HEIGHT];

        /**
         * Loaded COCO-SSD model instance
         * @type {object|null}
         * @private
         */
        this.detector = null;

        /**
         * Current object detection results
         * @type {Array}
         * @private
         */
        this.detections = [];

        /**
         * Flag for continuous detection mode
         * @type {boolean}
         * @private
         */
        this.continuous = false;

        /**
         * Detection processing frequency in milliseconds
         * @type {number}
         * @private
         */
        this.processFreq = 1000 / CONFIG.DEFAULT_FPS;

        /**
         * Color for drawing bounding boxes
         * @type {string}
         * @private
         */
        this.color = CONFIG.DEFAULT_BOX_COLOR;

        /**
         * Thickness of bounding box lines
         * @type {number}
         * @private
         */
        this.thickness = CONFIG.DEFAULT_BOX_THICKNESS;

        /**
         * Hidden canvas for drawing bounding boxes
         * @type {HTMLCanvasElement|null}
         * @private
         */
        this._canvas = null;

        /**
         * 2D rendering context for the canvas
         * @type {CanvasRenderingContext2D|null}
         * @private
         */
        this._ctx = null;

        // Initialize the extension
        this._setupCanvas();
        this._enableVideo();
    }

    /**
     * Get extension metadata including blocks, menus, and display information.
     * @returns {object} Extension metadata object
     */
    getInfo() {
        return {
            id: "objectdetection",
            name: formatMessage({
                id: "objectdetection.categoryName",
                default: "Object Detection",
                description:
                    "Label for the object detection extension category",
            }),
            blockIconURI: blockIconURI,
            blocks: [
                // Video Control Blocks
                {
                    opcode: "videoToggle",
                    text: formatMessage({
                        id: "objectdetection.videoToggle",
                        default: "turn video [VIDEO_STATE]",
                        description:
                            "Control the display of the camera video preview",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: "VIDEO_STATE",
                            defaultValue: VideoState.ON,
                        },
                    },
                },
                {
                    opcode: "setVideoTransparency",
                    text: formatMessage({
                        id: "objectdetection.setVideoTransparency",
                        default: "set video transparency to [TRANSPARENCY]%",
                        description:
                            "Adjust the opacity of the camera video preview (0-100)",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_VIDEO_TRANSPARENCY,
                        },
                    },
                },

                // Detection Configuration Blocks
                {
                    opcode: "setFrameRate",
                    text: formatMessage({
                        id: "objectdetection.setFrameRate",
                        default: "set detection rate to [FPS] fps",
                        description:
                            "Set how many times per second to run object detection",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        FPS: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_FPS,
                        },
                    },
                },
                {
                    opcode: "setBoxColor",
                    text: formatMessage({
                        id: "objectdetection.setBoxColor",
                        default: "set box color to [COLOR]",
                        description:
                            "Set the color of bounding boxes drawn around detected objects",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        COLOR: {
                            type: ArgumentType.STRING,
                            defaultValue: CONFIG.DEFAULT_BOX_COLOR,
                        },
                    },
                },
                {
                    opcode: "setBoxThickness",
                    text: formatMessage({
                        id: "objectdetection.setBoxThickness",
                        default: "set box thickness to [THICKNESS]",
                        description: "Set the line thickness of bounding boxes",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        THICKNESS: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_BOX_THICKNESS,
                        },
                    },
                },

                // Detection Control Blocks
                {
                    opcode: "detectObjectForTime",
                    text: formatMessage({
                        id: "objectdetection.detectObjectForTime",
                        default: "detect objects for [SECONDS] seconds",
                        description:
                            "Run object detection for a specified number of seconds",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        SECONDS: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1,
                        },
                    },
                },
                {
                    opcode: "toggleContinuousDetection",
                    text: formatMessage({
                        id: "objectdetection.toggleContinuousDetection",
                        default: "continuous detection [STATE]",
                        description:
                            "Enable or disable continuous object detection",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: "ON_OFF",
                            defaultValue: VideoState.ON,
                        },
                    },
                },

                // Detection Query Blocks
                {
                    opcode: "isCategoryDetected",
                    text: formatMessage({
                        id: "objectdetection.isCategoryDetected",
                        default: "is [CATEGORY] detected?",
                        description:
                            "Check if a specific object category is currently detected",
                    }),
                    blockType: BlockType.BOOLEAN,
                    arguments: {
                        CATEGORY: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "person",
                        },
                    },
                },
                {
                    opcode: "getBoundingBox",
                    text: formatMessage({
                        id: "objectdetection.getBoundingBox",
                        default: "get [DIMENSION] of [INDEX] object",
                        description:
                            "Get position or size information for a detected object",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        DIMENSION: {
                            type: ArgumentType.STRING,
                            menu: "DIMENSION",
                            defaultValue: BoundingBoxDimension.X,
                        },
                        INDEX: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0,
                        },
                    },
                },

                // Distance and Angle Measurement Blocks
                {
                    opcode: "distanceToCategory",
                    text: formatMessage({
                        id: "objectdetection.distanceToCategory",
                        default: "distance from [CATEGORY] to x: [X] y: [Y]",
                        description:
                            "Calculate distance from a detected object to a specific point",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        CATEGORY: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "person",
                        },
                        X: { type: ArgumentType.NUMBER, defaultValue: 0 },
                        Y: { type: ArgumentType.NUMBER, defaultValue: 0 },
                    },
                },
                {
                    opcode: "distanceBetweenCategory",
                    text: formatMessage({
                        id: "objectdetection.distanceBetweenCategory",
                        default: "distance from [CATEGORY1] to [CATEGORY2]",
                        description:
                            "Calculate distance between two detected object categories",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        CATEGORY1: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "person",
                        },
                        CATEGORY2: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "cat",
                        },
                    },
                },
                {
                    opcode: "angleToCategory",
                    text: formatMessage({
                        id: "objectdetection.angleToCategory",
                        default: "angle from [CATEGORY] to x: [X] y: [Y]",
                        description:
                            "Calculate angle from a detected object to a specific point",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        CATEGORY: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "person",
                        },
                        X: { type: ArgumentType.NUMBER, defaultValue: 0 },
                        Y: { type: ArgumentType.NUMBER, defaultValue: 0 },
                    },
                },
                {
                    opcode: "angleBetweenCategory",
                    text: formatMessage({
                        id: "objectdetection.angleBetweenCategory",
                        default: "angle from [CATEGORY1] to [CATEGORY2]",
                        description:
                            "Calculate angle between two detected object categories",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        CATEGORY1: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "person",
                        },
                        CATEGORY2: {
                            type: ArgumentType.STRING,
                            menu: "COCO_CATEGORIES",
                            defaultValue: "cat",
                        },
                    },
                },
                {
                    opcode: "distanceToIndex",
                    text: formatMessage({
                        id: "objectdetection.distanceToIndex",
                        default: "distance from box [INDEX] to x: [X] y: [Y]",
                        description:
                            "Calculate distance from a specific detected object to a point",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        INDEX: { type: ArgumentType.NUMBER, defaultValue: 0 },
                        X: { type: ArgumentType.NUMBER, defaultValue: 0 },
                        Y: { type: ArgumentType.NUMBER, defaultValue: 0 },
                    },
                },
                {
                    opcode: "angleToIndex",
                    text: formatMessage({
                        id: "objectdetection.angleToIndex",
                        default: "angle from box [INDEX] to x: [X] y: [Y]",
                        description:
                            "Calculate angle from a specific detected object to a point",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        INDEX: { type: ArgumentType.NUMBER, defaultValue: 0 },
                        X: { type: ArgumentType.NUMBER, defaultValue: 0 },
                        Y: { type: ArgumentType.NUMBER, defaultValue: 0 },
                    },
                },
            ],
            menus: {
                VIDEO_STATE: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: "objectdetection.videoState.on",
                                default: "on",
                            }),
                            value: VideoState.ON,
                        },
                        {
                            text: formatMessage({
                                id: "objectdetection.videoState.off",
                                default: "off",
                            }),
                            value: VideoState.OFF,
                        },
                    ],
                },
                DIMENSION: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: "objectdetection.dimension.x",
                                default: "x",
                            }),
                            value: BoundingBoxDimension.X,
                        },
                        {
                            text: formatMessage({
                                id: "objectdetection.dimension.y",
                                default: "y",
                            }),
                            value: BoundingBoxDimension.Y,
                        },
                        {
                            text: formatMessage({
                                id: "objectdetection.dimension.width",
                                default: "width",
                            }),
                            value: BoundingBoxDimension.WIDTH,
                        },
                        {
                            text: formatMessage({
                                id: "objectdetection.dimension.height",
                                default: "height",
                            }),
                            value: BoundingBoxDimension.HEIGHT,
                        },
                    ],
                },
                COCO_CATEGORIES: {
                    acceptReporters: true,
                    items: COCO_CATEGORIES,
                },
                ON_OFF: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: "objectdetection.onOff.on",
                                default: "on",
                            }),
                            value: VideoState.ON,
                        },
                        {
                            text: formatMessage({
                                id: "objectdetection.onOff.off",
                                default: "off",
                            }),
                            value: VideoState.OFF,
                        },
                    ],
                },
            },
        };
    }

    // === VIDEO CONTROL METHODS ===

    /**
     * Control video preview display state.
     * Block implementation for "turn video [VIDEO_STATE]".
     * @param {object} args - Block arguments
     * @param {string} args.VIDEO_STATE - Desired video state (on/off)
     */
    videoToggle(args) {
        if (!this.runtime.ioDevices || !this.runtime.ioDevices.video) return;

        if (args.VIDEO_STATE === VideoState.ON) {
            this.runtime.ioDevices.video.enableVideo();
        } else {
            this.runtime.ioDevices.video.disableVideo();
        }
    }

    /**
     * Set video preview transparency.
     * Block implementation for "set video transparency to [TRANSPARENCY]%".
     * @param {object} args - Block arguments
     * @param {number} args.TRANSPARENCY - Transparency percentage (0-100)
     */
    setVideoTransparency(args) {
        if (!this.runtime.ioDevices || !this.runtime.ioDevices.video) return;

        let transparency = Cast.toNumber(args.TRANSPARENCY);
        transparency = Math.max(0, Math.min(100, transparency));
        this.runtime.ioDevices.video.setVideoTransparency(transparency);
    }

    /**
     * Enable video preview on extension startup.
     * @private
     */
    _enableVideo() {
        if (this.runtime.ioDevices && this.runtime.ioDevices.video) {
            this.runtime.ioDevices.video.enableVideo();
        }
    }

    // === CANVAS SETUP AND DRAWING METHODS ===

    /**
     * Set up the hidden canvas for drawing bounding boxes.
     * Creates an off-screen canvas element for processing detection visualizations.
     * @private
     */
    _setupCanvas() {
        if (!this._canvas) {
            this._canvas = document.createElement("canvas");
            this._canvas.width = this.DIMENSIONS[0];
            this._canvas.height = this.DIMENSIONS[1];
            this._canvas.style.position = "absolute";
            this._canvas.style.top = CONFIG.CANVAS_HIDDEN_POSITION;
            document.body.appendChild(this._canvas);
            this._ctx = this._canvas.getContext("2d");
        }
    }

    /**
     * Draw bounding boxes around detected objects.
     * @param {Array} detections - Array of detection results from COCO-SSD
     * @private
     */
    _drawBoxes(detections) {
        if (!this._ctx) return;

        // Clear previous drawings
        this._ctx.clearRect(0, 0, this.DIMENSIONS[0], this.DIMENSIONS[1]);
        this._ctx.save();

        // Set drawing style
        this._ctx.strokeStyle = this.color;
        this._ctx.lineWidth = this.thickness;
        this._ctx.fillStyle = this.color;
        this._ctx.font = CONFIG.LABEL_FONT;

        // Draw each detection
        for (const detection of detections) {
            const [x, y, width, height] = detection.bbox;

            // Draw bounding box
            this._ctx.strokeRect(x, y, width, height);

            // Draw class label
            if (detection.class) {
                this._ctx.fillText(detection.class, x, y + CONFIG.LABEL_OFFSET);
            }
        }

        this._ctx.restore();
    }

    /**
     * Clear all drawings from the canvas.
     * @private
     */
    _clearCanvas() {
        if (this._ctx) {
            this._ctx.clearRect(0, 0, this.DIMENSIONS[0], this.DIMENSIONS[1]);
        }
    }

    // === MODEL LOADING AND DETECTION METHODS ===

    /**
     * Load the COCO-SSD model asynchronously.
     * Downloads TensorFlow.js and COCO-SSD libraries from CDN if not already loaded.
     * @returns {Promise<object>} The loaded COCO-SSD model
     * @private
     */
    async _loadModel() {
        if (this.detector) return this.detector;

        // Load required libraries if not already present
        await new Promise((resolve, reject) => {
            if (window.cocoSsd && window.tf) return resolve();

            // Load TensorFlow.js first
            const tfScript = document.createElement("script");
            tfScript.src = CONFIG.TENSORFLOW_CDN_URL;
            tfScript.onload = () => {
                // Then load COCO-SSD model
                const cocoScript = document.createElement("script");
                cocoScript.src = CONFIG.COCO_SSD_CDN_URL;
                cocoScript.onload = resolve;
                cocoScript.onerror = reject;
                document.head.appendChild(cocoScript);
            };
            tfScript.onerror = reject;
            document.head.appendChild(tfScript);
        });

        // Load the model
        this.detector = await window.cocoSsd.load();
        return this.detector;
    }

    /**
     * Run object detection on a video frame using COCO-SSD.
     * @param {object} detector - The loaded COCO-SSD model
     * @param {ImageData} frame - Video frame data
     * @returns {Promise<Array>} Array of detection results
     * @private
     */
    async _detectOnFrame(detector, frame) {
        // Convert ImageData to Canvas for model input
        const canvas = document.createElement("canvas");
        canvas.width = frame.width;
        canvas.height = frame.height;
        const ctx = canvas.getContext("2d");
        ctx.putImageData(frame, 0, 0);

        // Run detection
        return await detector.detect(canvas);
    }

    /**
     * Perform object detection on the current video frame.
     * Updates the detections array and draws bounding boxes.
     * @private
     */
    async detectObject() {
        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);
        this.detections = predictions;
        this._drawBoxes(this.detections);
    }

    // === CONFIGURATION METHODS ===

    /**
     * Set the detection frame rate.
     * Block implementation for "set detection rate to [FPS] fps".
     * @param {object} args - Block arguments
     * @param {number} args.FPS - Frames per second for detection
     */
    setFrameRate(args) {
        const fps = Cast.toNumber(args.FPS);
        this.processFreq = 1000 / Math.max(1, fps);
    }

    /**
     * Set the color of bounding boxes.
     * Block implementation for "set box color to [COLOR]".
     * @param {object} args - Block arguments
     * @param {string} args.COLOR - CSS color string for bounding boxes
     */
    setBoxColor(args) {
        this.color = args.COLOR || CONFIG.DEFAULT_BOX_COLOR;
    }

    /**
     * Set the thickness of bounding box lines.
     * Block implementation for "set box thickness to [THICKNESS]".
     * @param {object} args - Block arguments
     * @param {number} args.THICKNESS - Line thickness in pixels
     */
    setBoxThickness(args) {
        this.thickness = Cast.toNumber(args.THICKNESS);
    }

    // === DETECTION CONTROL METHODS ===

    /**
     * Run object detection for a specified duration.
     * Block implementation for "detect objects for [SECONDS] seconds".
     * @param {object} args - Block arguments
     * @param {number} args.SECONDS - Duration to run detection
     */
    async detectObjectForTime(args) {
        const seconds = Cast.toNumber(args.SECONDS);
        const endTime = Date.now() + seconds * 1000;

        while (Date.now() < endTime) {
            await this.detectObject();
            await new Promise((resolve) =>
                setTimeout(resolve, this.processFreq)
            );
        }

        this._clearCanvas();
    }

    /**
     * Toggle continuous detection mode.
     * Block implementation for "continuous detection [STATE]".
     * @param {object} args - Block arguments
     * @param {string} args.STATE - Whether to enable (on) or disable (off) continuous detection
     */
    toggleContinuousDetection(args) {
        this.continuous = args.STATE === VideoState.ON;

        if (this.continuous) {
            this._continuousLoop();
        }
    }

    /**
     * Run continuous object detection until disabled.
     * @private
     */
    async _continuousLoop() {
        while (this.continuous) {
            await this.detectObject();
            await new Promise((resolve) =>
                setTimeout(resolve, this.processFreq)
            );
        }
        this._clearCanvas();
    }

    // === UTILITY METHODS FOR CALCULATIONS ===

    /**
     * Calculate the center point of a bounding box.
     * @param {number[]} bbox - Bounding box array [x, y, width, height]
     * @returns {object} Center coordinates {x, y}
     * @private
     */
    _getBoxCenter(bbox) {
        return {
            x: bbox[0] + bbox[2] / 2,
            y: bbox[1] + bbox[3] / 2,
        };
    }

    /**
     * Calculate Euclidean distance between two points.
     * @param {number} x1 - X coordinate of first point
     * @param {number} y1 - Y coordinate of first point
     * @param {number} x2 - X coordinate of second point
     * @param {number} y2 - Y coordinate of second point
     * @returns {number} Distance between the points
     * @private
     */
    _distance(x1, y1, x2, y2) {
        return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    }

    /**
     * Calculate angle between two points in degrees.
     * @param {number} x1 - X coordinate of first point
     * @param {number} y1 - Y coordinate of first point
     * @param {number} x2 - X coordinate of second point
     * @param {number} y2 - Y coordinate of second point
     * @returns {number} Angle in degrees (0-360)
     * @private
     */
    _angle(x1, y1, x2, y2) {
        const angleRad = Math.atan2(y2 - y1, x2 - x1);
        const angleDeg = angleRad * (180 / Math.PI);
        return (angleDeg + 360) % 360;
    }

    /**
     * Find the first detection of a specific object category.
     * @param {string} category - Object category to search for
     * @param {Array} predictions - Array of detection results
     * @returns {object|null} Detection object or null if not found
     * @private
     */
    _findCategoryDetection(category, predictions) {
        for (const prediction of predictions || []) {
            if (
                prediction.class &&
                prediction.class.toLowerCase() === category.toLowerCase()
            ) {
                return prediction;
            }
        }
        return null;
    }

    // === DETECTION QUERY METHODS ===

    /**
     * Get bounding box information for a detected object.
     * Block implementation for "get [DIMENSION] of [INDEX] object".
     * @param {object} args - Block arguments
     * @param {string} args.DIMENSION - Which dimension to return (x/y/width/height)
     * @param {number} args.INDEX - Index of the detected object
     * @returns {Promise<number>} The requested dimension value or 0 if not found
     */
    async getBoundingBox(args) {
        const index = Cast.toNumber(args.INDEX);
        const dimension = args.DIMENSION;

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);

        if (!predictions || index >= predictions.length) return 0;

        const bbox = predictions[index].bbox;

        switch (dimension) {
            case BoundingBoxDimension.X:
                return bbox[0];
            case BoundingBoxDimension.Y:
                return bbox[1];
            case BoundingBoxDimension.WIDTH:
                return bbox[2];
            case BoundingBoxDimension.HEIGHT:
                return bbox[3];
            default:
                return 0;
        }
    }

    /**
     * Check if a specific object category is currently detected.
     * Block implementation for "is [CATEGORY] detected?".
     * @param {object} args - Block arguments
     * @param {string} args.CATEGORY - Object category to check for
     * @returns {Promise<boolean>} True if the category is detected
     */
    async isCategoryDetected(args) {
        const category = args.CATEGORY;

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return false;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);

        return this._findCategoryDetection(category, predictions) !== null;
    }

    // === DISTANCE AND ANGLE MEASUREMENT METHODS ===

    /**
     * Calculate distance from a detected object category to a specific point.
     * Block implementation for "distance from [CATEGORY] to x: [X] y: [Y]".
     * @param {object} args - Block arguments
     * @param {string} args.CATEGORY - Object category to measure from
     * @param {number} args.X - Target X coordinate
     * @param {number} args.Y - Target Y coordinate
     * @returns {Promise<number>} Distance in pixels or 0 if object not found
     */
    async distanceToCategory(args) {
        const category = args.CATEGORY;
        const targetX = Cast.toNumber(args.X);
        const targetY = Cast.toNumber(args.Y);

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);
        const detection = this._findCategoryDetection(category, predictions);

        if (detection) {
            const center = this._getBoxCenter(detection.bbox);
            return this._distance(center.x, center.y, targetX, targetY);
        }

        return 0;
    }

    /**
     * Calculate distance between two detected object categories.
     * Block implementation for "distance from [CATEGORY1] to [CATEGORY2]".
     * @param {object} args - Block arguments
     * @param {string} args.CATEGORY1 - First object category
     * @param {string} args.CATEGORY2 - Second object category
     * @returns {Promise<number>} Distance in pixels or 0 if either object not found
     */
    async distanceBetweenCategory(args) {
        const category1 = args.CATEGORY1;
        const category2 = args.CATEGORY2;

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);

        const detection1 = this._findCategoryDetection(category1, predictions);
        const detection2 = this._findCategoryDetection(category2, predictions);

        if (detection1 && detection2) {
            const center1 = this._getBoxCenter(detection1.bbox);
            const center2 = this._getBoxCenter(detection2.bbox);
            return this._distance(center1.x, center1.y, center2.x, center2.y);
        }

        return 0;
    }

    /**
     * Calculate angle from a detected object category to a specific point.
     * Block implementation for "angle from [CATEGORY] to x: [X] y: [Y]".
     * @param {object} args - Block arguments
     * @param {string} args.CATEGORY - Object category to measure from
     * @param {number} args.X - Target X coordinate
     * @param {number} args.Y - Target Y coordinate
     * @returns {Promise<number>} Angle in degrees (0-360) or 0 if object not found
     */
    async angleToCategory(args) {
        const category = args.CATEGORY;
        const targetX = Cast.toNumber(args.X);
        const targetY = Cast.toNumber(args.Y);

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);
        const detection = this._findCategoryDetection(category, predictions);

        if (detection) {
            const center = this._getBoxCenter(detection.bbox);
            return this._angle(center.x, center.y, targetX, targetY);
        }

        return 0;
    }

    /**
     * Calculate angle between two detected object categories.
     * Block implementation for "angle from [CATEGORY1] to [CATEGORY2]".
     * @param {object} args - Block arguments
     * @param {string} args.CATEGORY1 - First object category
     * @param {string} args.CATEGORY2 - Second object category
     * @returns {Promise<number>} Angle in degrees (0-360) or 0 if either object not found
     */
    async angleBetweenCategory(args) {
        const category1 = args.CATEGORY1;
        const category2 = args.CATEGORY2;

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);

        const detection1 = this._findCategoryDetection(category1, predictions);
        const detection2 = this._findCategoryDetection(category2, predictions);

        if (detection1 && detection2) {
            const center1 = this._getBoxCenter(detection1.bbox);
            const center2 = this._getBoxCenter(detection2.bbox);
            return this._angle(center1.x, center1.y, center2.x, center2.y);
        }

        return 0;
    }

    /**
     * Calculate distance from a specific detected object (by index) to a point.
     * Block implementation for "distance from box [INDEX] to x: [X] y: [Y]".
     * @param {object} args - Block arguments
     * @param {number} args.INDEX - Index of the detected object
     * @param {number} args.X - Target X coordinate
     * @param {number} args.Y - Target Y coordinate
     * @returns {Promise<number>} Distance in pixels or 0 if object not found
     */
    async distanceToIndex(args) {
        const index = Cast.toNumber(args.INDEX);
        const targetX = Cast.toNumber(args.X);
        const targetY = Cast.toNumber(args.Y);

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);

        if (!predictions || index >= predictions.length) return 0;

        const center = this._getBoxCenter(predictions[index].bbox);
        return this._distance(center.x, center.y, targetX, targetY);
    }

    /**
     * Calculate angle from a specific detected object (by index) to a point.
     * Block implementation for "angle from box [INDEX] to x: [X] y: [Y]".
     * @param {object} args - Block arguments
     * @param {number} args.INDEX - Index of the detected object
     * @param {number} args.X - Target X coordinate
     * @param {number} args.Y - Target Y coordinate
     * @returns {Promise<number>} Angle in degrees (0-360) or 0 if object not found
     */
    async angleToIndex(args) {
        const index = Cast.toNumber(args.INDEX);
        const targetX = Cast.toNumber(args.X);
        const targetY = Cast.toNumber(args.Y);

        const frame = this.runtime.ioDevices.video.getFrame({
            format: "image-data",
            dimensions: this.DIMENSIONS,
        });

        if (!frame) return 0;

        const detector = await this._loadModel();
        const predictions = await this._detectOnFrame(detector, frame);

        if (!predictions || index >= predictions.length) return 0;

        const center = this._getBoxCenter(predictions[index].bbox);
        return this._angle(center.x, center.y, targetX, targetY);
    }
}

module.exports = Scratch3ObjectDetectionBlocks;
